import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useState } from "react";

import FieldCreateEditCard from "./FieldCreateEditCard";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../../hooks/useResponsive";
import { TooltipName } from "../../../../utils/Enums";
import { CustomButton } from "../../../../components/Button/Button";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";
import { isAdmin } from "../../../../utils/constants";
import { useCreateCardMutation } from "./hooks/useCreateCardMutation";

function CreateCard({
  stage,
  isTodo = false,
  newCreatedCardId,
  isCreateCardFieldOpen,
  handleCreateCardClick,
  parentCardId = null,
  boardName
}) {
  const { id } = useBoardIdByKey();
  const [loading, setLoading] = useState(false);
  const [createBtnOpen, setCreateBtnOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [milestone] = useCustomSearchParams("milestone");
  const isMobile = useResponsive("down", "md");
  const boardPermission= boardName?.board_permission;
  
  // Use refs to persist state across tab switches
  const titleRef = React.useRef("");
  const createBtnOpenRef = React.useRef(false);
  const isTabVisibleRef = React.useRef(true);

  const { mutate: createMutate } = useCreateCardMutation({
    boardId: id,
    boardName,
    isTodo,
    parentCardId,
    milestone,
    onSuccess: (newCardId) => {
      newCreatedCardId?.(newCardId);
      setLoading(false);
      setTitle("");
      setCreateBtnOpen(false);
    },
    onError: (error) => {
      console.error("Error creating card", error);
      setLoading(false);
    },
  });

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleEnterKeyPress();
    }
    if (event.key === "btnClicked") {
      handleEnterKeyPress();
    }
  };

  const handleEnterKeyPress = () => {
    // Don't allow creating if already loading or title is empty
    if (loading || !title?.trim()) {
      return;
    }

    handleCreateCardClick?.(null, stage.toString());
    const data = {
      title,
      board_id: id,
      ...(milestone && { epic_id: milestone }),
      ...(isTodo
        ? { is_todo: true, todo_column: "Open" }
        : { workflow_step_id: stage }),
      ...(parentCardId ? { parent_card_id: parentCardId } : {}),
    };

    setLoading(true);
    createMutate(data);
  };

  useEffect(() => {
    setCreateBtnOpen(isCreateCardFieldOpen);
    createBtnOpenRef.current = isCreateCardFieldOpen;
  }, [isCreateCardFieldOpen]);

  // Sync title with ref
  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  // Handle tab visibility changes to preserve input field state
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isTabVisibleRef.current = isVisible;
      
      // When tab becomes visible again, restore the field state if user had typed something
      if (isVisible && titleRef.current) {
        // Restore field if user had typed something before switching tabs
        setCreateBtnOpen(true);
        createBtnOpenRef.current = true;
        setTitle(titleRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);


  return (
    <div style={isMobile ? { width: "100%" } : {}}>
      {createBtnOpen ? (
        <div
          style={{ marginBottom: "10px", width: isMobile ? "100%" : "auto" }}
        >
          <FieldCreateEditCard
            handleKeyDown={handleKeyDown}
            setTitle={setTitle}
            value={title}
            setCreateBtnOpen={(shouldClose) => {
              // Only close if tab is visible (not switching tabs) or if explicitly requested
              if (shouldClose !== false && isTabVisibleRef.current) {
                setCreateBtnOpen(false);
                createBtnOpenRef.current = false;
              }
            }}
            preventCloseOnTabSwitch={true}
            loading={loading}
            disabled={loading}
          />
          {/* <FormControl fullWidth>
            <TextareaAutosize
              aria-label="empty textarea"
              placeholder="What needs to be done?"
              minRows={4} // Set the minimum number of rows
              className="create-card-textarea"
              onKeyDown={handleKeyDown}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setCreateBtnOpen(false)}
              autoFocus
            />
            <InputAdornment position="end">
              <img src={CheckBox} className=" mb-6" />
              <ExpandMoreIcon className="mb-6" />
            </InputAdornment>
          </FormControl> */}
        </div>
      ) : (
        id &&
        !isMobile && (boardPermission?.can_create_task || boardPermission?.is_admin || isAdmin() || boardName?.is_owner) && (
          <div className="d-flex justify-content-end create-card-div">
            <Tooltip
              title={
                isTodo
                  ? TooltipName.BOARD_PAGE.CREATE_QUICK_TASK
                  : TooltipName.BOARD_PAGE.CREATE_TASK
              }
            >
              <CustomButton
                size="small"
                className="create-card-btn create-card-btn-bg reset-btn"
                onClick={() => {
                  setCreateBtnOpen(true);
                  createBtnOpenRef.current = true;
                }}
              >
                <AddIcon className="create-card-btn-icon" />
              </CustomButton>
            </Tooltip>
          </div>
        )
      )}
    </div>
  );
}

export default CreateCard;
